import React, { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist/build/pdf";

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.js`;

// One rendered page: canvas + clickable link overlays that open in a new tab.
const PdfPage = ({ pdf, pageNumber, width }) => {
  const canvasRef = useRef(null);
  const [links, setLinks] = useState([]);
  const [size, setSize] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let task = null;
    (async () => {
      const page = await pdf.getPage(pageNumber);
      const base = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale: width / base.width });
      const ratio = window.devicePixelRatio || 1;
      const canvas = canvasRef.current;
      if (!canvas || cancelled) return;
      canvas.width = Math.floor(viewport.width * ratio);
      canvas.height = Math.floor(viewport.height * ratio);
      setSize({ w: viewport.width, h: viewport.height });
      task = page.render({
        canvasContext: canvas.getContext("2d"),
        viewport,
        transform: ratio !== 1 ? [ratio, 0, 0, ratio, 0, 0] : null,
      });
      await task.promise.catch(() => {});
      const annots = await page.getAnnotations();
      if (cancelled) return;
      setLinks(
        annots
          .filter((a) => a.subtype === "Link" && a.url)
          .map((a) => {
            const [x1, y1, x2, y2] = viewport.convertToViewportRectangle(a.rect);
            return {
              url: a.url,
              left: Math.min(x1, x2),
              top: Math.min(y1, y2),
              w: Math.abs(x2 - x1),
              h: Math.abs(y2 - y1),
            };
          })
      );
    })();
    return () => {
      cancelled = true;
      task?.cancel();
    };
  }, [pdf, pageNumber, width]);

  return (
    <div
      className="relative bg-white shadow-sm mx-auto"
      style={size ? { width: size.w, height: size.h } : { width }}
      data-testid={`preview-page-${pageNumber}`}
    >
      <canvas ref={canvasRef} style={size ? { width: size.w, height: size.h } : undefined} />
      {links.map((l, i) => (
        <a
          key={i}
          href={l.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="preview-pdf-link"
          className="absolute block hover:bg-black/5"
          style={{ left: l.left, top: l.top, width: l.w, height: l.h }}
        />
      ))}
    </div>
  );
};

export const PdfPreview = ({ url, height }) => {
  const wrapRef = useRef(null);
  const [pdf, setPdf] = useState(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    let doc = null;
    let cancelled = false;
    const task = pdfjsLib.getDocument(url);
    task.promise.then((d) => {
      doc = d;
      if (!cancelled) setPdf(d);
    }).catch(() => {});
    return () => {
      cancelled = true;
      task.destroy();
      doc?.destroy();
    };
  }, [url]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => {
      const pad = parseFloat(getComputedStyle(el).paddingLeft || "0") * 2;
      if (el.clientWidth > 0) setWidth(Math.max(200, el.clientWidth - pad));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={wrapRef}
      data-testid="preview-iframe"
      className={`w-full border border-neutral-200 bg-neutral-100 rounded-sm space-y-3 ${height ? "overflow-auto p-4" : "p-2"}`}
      style={height ? { height, minHeight: "600px" } : { minHeight: "300px" }}
    >
      {pdf && width > 0 &&
        Array.from({ length: pdf.numPages }, (_, i) => (
          <PdfPage key={`${url}-${i}`} pdf={pdf} pageNumber={i + 1} width={width} />
        ))}
    </div>
  );
};
